import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { rhm, apiError, dt } from "../api";
import { useRhmAuth } from "../RhmAuthContext";
import { Badge, Btn, Card, Empty, Field, Input, Modal, PageHead, Select, Table, Td } from "../ui";

export default function RhmUsers() {
    const { can, user: me } = useRhmAuth();
    const qc = useQueryClient();
    const [form, setForm] = useState(null);

    const { data: users = [], isLoading } = useQuery({
        queryKey: ["rhm-users"],
        queryFn: async () => (await rhm.get("/users")).data,
    });
    const { data: roles = [] } = useQuery({
        queryKey: ["rhm-roles"],
        queryFn: async () => (await rhm.get("/roles")).data,
    });
    const { data: registry } = useQuery({
        queryKey: ["rhm-permission-registry"],
        queryFn: async () => (await rhm.get("/auth/permission-registry")).data,
    });

    const invalidate = () => qc.invalidateQueries({ queryKey: ["rhm-users"] });

    const create = useMutation({
        mutationFn: async (payload) => (await rhm.post("/users", payload)).data,
        onSuccess: () => { toast.success("User created"); setForm(null); invalidate(); },
        onError: (e) => toast.error(apiError(e)),
    });
    const patch = useMutation({
        mutationFn: async ({ id, payload }) => (await rhm.patch(`/users/${id}`, payload)).data,
        onSuccess: () => { toast.success("User updated"); setForm(null); invalidate(); },
        onError: (e) => toast.error(apiError(e)),
    });
    const access = useMutation({
        mutationFn: async ({ id, is_active }) => (await rhm.post(`/users/${id}/access`, { is_active })).data,
        onSuccess: (d) => { toast.success(d.is_active ? "Access restored" : "Access revoked"); invalidate(); },
        onError: (e) => toast.error(apiError(e)),
    });

    const openNew = () => setForm({ name: "", email: "", password: "", role_key: "manager", department: "Sales", phone: "", is_active: true });

    return (
        <div data-testid="rhm-users-page">
            <PageHead title="Users" sub="Staff accounts, role assignment and access control."
                action={can("users.create") && (
                    <Btn onClick={openNew} data-testid="rhm-add-user">
                        <Plus className="w-3.5 h-3.5 inline mr-1.5" /> ADD USER
                    </Btn>
                )} />

            <Card title={`${users.length} user(s)`}>
                {isLoading ? <div className="mono text-xs text-zinc-500">LOADING…</div> : (
                    <Table testId="rhm-users-table" head={["Name", "Email", "Role", "Department", "Last Login", "Access", "Actions"]}
                        empty={!users.length && <Empty msg="No users." />}>
                        {users.map((u) => (
                            <tr key={u.id} data-testid={`rhm-user-row-${u.email}`}>
                                <Td className="font-semibold">{u.name}{u.id === me?.id && <span className="text-zinc-500 text-xs"> (you)</span>}</Td>
                                <Td className="text-sm text-zinc-400">{u.email}</Td>
                                <Td><Badge tone={u.role_key === "super_admin" ? "gold" : "muted"}>{u.role_name}</Badge></Td>
                                <Td className="text-sm text-zinc-400">{u.department || "—"}</Td>
                                <Td className="text-xs text-zinc-500">{u.last_login ? dt(u.last_login) : "never"}</Td>
                                <Td><Badge tone={u.is_active ? "good" : "danger"}>{u.is_active ? "ACTIVE" : "REVOKED"}</Badge></Td>
                                <Td>
                                    <div className="flex gap-3">
                                        {can("users.edit") && (
                                            <button onClick={() => setForm({ ...u, password: "" })} data-testid={`rhm-edit-user-${u.email}`}
                                                className="mono text-[10px] tracking-[0.16em] text-gold">EDIT</button>
                                        )}
                                        {me?.is_super_admin && u.id !== me.id && (
                                            <button onClick={() => access.mutate({ id: u.id, is_active: !u.is_active })}
                                                data-testid={`rhm-toggle-access-${u.email}`}
                                                className={`mono text-[10px] tracking-[0.16em] ${u.is_active ? "text-red-400" : "text-lime"}`}>
                                                {u.is_active ? "REVOKE" : "RESTORE"}
                                            </button>
                                        )}
                                    </div>
                                </Td>
                            </tr>
                        ))}
                    </Table>
                )}
                {!me?.is_super_admin && (
                    <div className="mt-4 mono text-[10px] tracking-[0.14em] text-zinc-500">
                        REVOKING USER ACCESS IS RESTRICTED TO THE SUPER ADMIN
                    </div>
                )}
            </Card>

            <Modal open={!!form} onClose={() => setForm(null)} title={form?.id ? "Edit User" : "Add User"} testId="rhm-user-modal">
                {form && (
                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            if (form.id) {
                                const payload = { name: form.name, role_key: form.role_key, department: form.department, phone: form.phone };
                                if (form.password) payload.password = form.password;
                                patch.mutate({ id: form.id, payload });
                            } else {
                                create.mutate(form);
                            }
                        }}
                        className="space-y-4"
                    >
                        <div className="grid md:grid-cols-2 gap-4">
                            <Field label="Full name" required><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name" /></Field>
                            <Field label="Work email" required>
                                <Input type="email" required disabled={!!form.id} value={form.email}
                                    onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email" />
                            </Field>
                            <Field label="Role" required>
                                <Select required value={form.role_key} onChange={(e) => setForm({ ...form, role_key: e.target.value })} data-testid="user-role">
                                    {roles.filter((r) => r.key !== "super_admin" || me?.is_super_admin)
                                        .map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
                                </Select>
                            </Field>
                            <Field label="Assigned department" hint="Scopes what a Manager sees">
                                <Select value={form.department || ""} onChange={(e) => setForm({ ...form, department: e.target.value })} data-testid="user-department">
                                    <option value="">None</option>
                                    {(registry?.departments || []).map((d) => <option key={d} value={d}>{d}</option>)}
                                </Select>
                            </Field>
                            <Field label="Phone"><Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
                            <Field label={form.id ? "Reset password" : "Password"} required={!form.id} hint="Minimum 8 characters">
                                <Input type="password" required={!form.id} value={form.password || ""}
                                    onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password" />
                            </Field>
                        </div>
                        <div className="flex gap-3">
                            <Btn type="submit" disabled={create.isPending || patch.isPending} data-testid="user-submit">
                                {form.id ? "SAVE USER" : "CREATE USER"}
                            </Btn>
                            <Btn type="button" variant="ghost" onClick={() => setForm(null)}>CANCEL</Btn>
                        </div>
                    </form>
                )}
            </Modal>
        </div>
    );
}
